/* istanbul ignore file */

import {
  Component,
  EventEmitter,
  Inject,
  Input,
  LOCALE_ID,
  OnInit,
  Output,
  computed,
  input,
  output,
  signal,
} from '@angular/core';
import { Functions } from '@angular/fire/functions';

import { buyTicket } from '@yoimo/client-sdk/events';
import { buySubscription } from '@yoimo/client-sdk/subscriptions';
import {
  BillingPeriodicity,
  PriceWithTaxCalculation,
  StripeAddress,
} from '@yoimo/interfaces';
import { ToastService } from '@yoimo/joymo-ui';

import { EMPTY, Observable, catchError, map, take } from 'rxjs';

import { logger } from '@core/services';

import { PaymentDetails } from '../../payments/capture-payment-method/capture-payment-method.component';
import { formatDate } from '../data-transformers';
import {
  AdditionalPaymentConfig,
  PlanOption,
  SubscriptionCardDetails,
  TicketOption,
} from '../ticket-options-types';

type SubscriptionOptionInfo = {
  // Title like Xdays trial or Installment
  emphasized: string[];
  body: string[];
  canCancelAnytime: boolean;
};

@Component({
  selector: 'joymo-payment-handler',
  templateUrl: './payment-handler.component.html',
  styleUrls: ['./payment-handler.component.scss'],
})
export class PaymentHandlerComponent implements OnInit {
  additionalConfig = input.required<AdditionalPaymentConfig>();
  channelId = input.required<string | undefined>();
  option = input.required<TicketOption | PlanOption>();
  eventId = computed<string | undefined>(() => {
    const option = this.option();
    return option.type === 'TICKET' ? option.eventId : undefined;
  });
  selectedPlanPrice = computed<
    SubscriptionCardDetails['planPrices'][number] | undefined
  >(() => {
    const option = this.option();
    const additionalConfig = this.additionalConfig();
    if (option.type !== 'PLAN' || !additionalConfig.periodicity) {
      return undefined;
    }
    const selected = option.cardDetails.planPrices.find(
      (s) => s.periodicity === additionalConfig.periodicity
    );
    if (!selected) {
      return undefined;
    }
    return selected;
  });
  optionHasTrial = computed<boolean>(() => {
    const planPrice = this.selectedPlanPrice();
    return !!planPrice?.trialPeriod;
  });
  optionName = computed(() => {
    const option = this.option();
    return 'ticketData' in option
      ? option.ticketData.name
      : option.planData.name;
  });
  subscriptionInfo = computed<SubscriptionOptionInfo | null>(() => {
    const option = this.option();
    const additionalConfig = this.additionalConfig();
    const selectedPlanPrice = this.selectedPlanPrice();
    if (option?.type !== 'PLAN' || !additionalConfig || !selectedPlanPrice) {
      return null;
    }
    return this.getSubscriptionOptionInfo(
      option,
      selectedPlanPrice,
      additionalConfig
    );
  });
  videoId = input<string | undefined>();
  showPreviewStep = input<boolean>(true);
  // Save the payment details for capturing as a second step for the preview
  readonly pricePreview = signal<{
    calculation: PriceWithTaxCalculation;
    params: Parameters<PaymentHandlerComponent['buyOrPreviewOption']>;
  } | null>(null);
  readonly processingPayment = signal<boolean>(false);

  @Output() onActionResult = new EventEmitter<'success' | 'failure'>();
  onTaxCalulcationAvailable = output<{
    type: 'FINAL' | 'ESTIMATE';
    calculation: PriceWithTaxCalculation;
  }>();
  readonly title = $localize`:@@paymentHandlerTitle:Payment details`;

  constructor(
    private ff: Functions,
    private toastService: ToastService,
    @Inject(LOCALE_ID) public locale: string
  ) {}

  ngOnInit(): void {
    if (!this.channelId()) {
      throw new TypeError('No channel ID specified for payment');
    }
    if (!this.option()) {
      throw new TypeError('No option selected for payment');
    }
  }

  formatRate(
    rate: PriceWithTaxCalculation['taxItems'][number]['rate']
  ): string {
    return 'percentageDecimal' in rate
      ? ', ' + rate.percentageDecimal + '%'
      : '';
  }

  buyOrPreviewOption(
    paymentDetails: PaymentDetails,
    option: TicketOption | PlanOption,
    skipPreview?: boolean
  ): void {
    this.processingPayment.set(true);
    // Force a dry run of the payment function to return the full breakdown of the purchase
    const forcePreview = skipPreview ? false : this.showPreviewStep();

    this.finalizePayment$(paymentDetails, option, forcePreview)
      .pipe(
        catchError((e) => {
          logger.logCriticalError(e, 'PAYMENTS');
          this.onActionResult.next('failure');
          this.toastService.open(
            $localize`:@@paymentHandlerErrorToastMessage:An error has occurred`,
            { type: 'error' }
          );
          return EMPTY;
        }),
        take(1)
      )
      .subscribe((pricePreviewOrNull) => {
        // this is supposedly a success so check if we should show the preview
        if (pricePreviewOrNull !== null) {
          // This is a preview
          this.onTaxCalulcationAvailable.emit({
            type: 'FINAL',
            calculation: pricePreviewOrNull,
          });
          this.pricePreview.set({
            calculation: pricePreviewOrNull,
            params: [paymentDetails, option],
          });
          this.processingPayment.set(false);
        } else {
          this.onActionResult.next('success');
          this.toastService.open(
            $localize`:@@paymentHandlerSuccessToastMessage:Payment successful`,
            { type: 'success' }
          );
        }
      });
  }

  private finalizePayment$(
    paymentDetails: PaymentDetails,
    selectedOption: TicketOption | PlanOption,
    dryRun: boolean
  ): Observable<PriceWithTaxCalculation | null> {
    const additionalConfig = this.additionalConfig();
    const channelId = this.channelId();

    // Sanity checks
    if (!channelId) {
      throw new TypeError('No channelId provided');
    }

    if (!paymentDetails.billingAddress) {
      throw new TypeError('No address provided');
    }

    if (selectedOption.type === 'PLAN') {
      if (!additionalConfig.periodicity) {
        throw new TypeError('No periodicity provided');
      }

      return this.buySubscription$(
        channelId,
        selectedOption.planData.docId,
        selectedOption.planData.priceAlternatives[0].currency,
        additionalConfig.periodicity,
        paymentDetails.paymentId,
        additionalConfig.coupon,
        !!additionalConfig.useInstallments,
        paymentDetails.billingAddress,
        dryRun
      );
    } else {
      const eventId = this.eventId();
      const videoId = this.videoId();
      if (!eventId) {
        throw new TypeError('No event ID specified');
      }

      return this.buySingleTicket$(
        channelId,
        eventId,
        selectedOption.ticketData.id,
        videoId,
        paymentDetails.paymentId,
        selectedOption.optionPrice?.currency,
        paymentDetails.billingAddress!,
        additionalConfig?.coupon,
        dryRun
      );
    }
  }

  /** @returns SoldTicket ID */
  private buySingleTicket$(
    channelId: string,
    eventId: string,
    ticketId: string,
    videoId: string | undefined,
    paymentMethodId: string,
    currency: string | undefined,
    address: StripeAddress,
    _coupon: string | undefined,
    dryRun: boolean
  ): ReturnType<PaymentHandlerComponent['finalizePayment$']> {
    return buyTicket(
      this.ff,
      channelId,
      eventId,
      ticketId,
      paymentMethodId,
      videoId,
      currency,
      address,
      dryRun
    ).pipe(
      map((result) => {
        // Dry run result
        if ('preview' in result) {
          return result.preview;
        }
        if ('success' in result) {
          if (!result.success) {
            throw new Error(result.errorReason);
          }
          return null;
        }
        throw new Error('Not implemented');
      })
    );
  }

  /** @returns Subscription ID */
  private buySubscription$(
    channelId: string,
    planId: string,
    currency: string,
    selectedPeriodicity: BillingPeriodicity,
    paymentMethodId: string,
    coupon: string | undefined,
    useInstallments: boolean,
    address: StripeAddress,
    dryRun: boolean
  ): ReturnType<PaymentHandlerComponent['finalizePayment$']> {
    return buySubscription(
      this.ff,
      channelId,
      planId,
      selectedPeriodicity,
      paymentMethodId,
      currency,
      address,
      dryRun,
      coupon,
      useInstallments
    ).pipe(
      map((result) => {
        if ('preview' in result) {
          return result.preview;
        }
        if ('success' in result) {
          if (!result.success) {
            throw new Error(result.error);
          }
          return null;
        }
        throw new Error('Not implemented');
      })
    );
  }

  onPriceEstimateAvailable(calculation: PriceWithTaxCalculation): void {
    this.onTaxCalulcationAvailable.emit({ type: 'ESTIMATE', calculation });
  }

  getSubscriptionOptionInfo(
    option: PlanOption,
    selectedPlanPrice: SubscriptionCardDetails['planPrices'][number],
    additionalConfig: AdditionalPaymentConfig
  ): SubscriptionOptionInfo {
    const result: SubscriptionOptionInfo = {
      emphasized: [],
      body: ['This is a recurring subscription and will renew automatically.'],
      canCancelAnytime: !additionalConfig.useInstallments,
    };

    if (additionalConfig.useInstallments) {
      result.emphasized.push('Installment payment.');
      result.body.push(
        'Please note that you cannot cancel individual payments until all installments have been paid.'
      );
    } else {
      let chargedToday = true;
      const startChargingDate = new Date();
      if (selectedPlanPrice.trialPeriod) {
        result.emphasized.push(selectedPlanPrice.trialPeriod);
        startChargingDate.setDate(
          startChargingDate.getDate() +
            (selectedPlanPrice._effectivePlanPriceData.trialDays ?? 0)
        );
        chargedToday = false;
      }
      result.body.push(
        `You will be charged starting ${chargedToday ? 'today' : formatDate(this.locale, startChargingDate)}.`
      );
    }
    return result;
  }
}
